import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Typography, Button, List, Tag, Space } from 'antd';
import {
  FileTextOutlined,
  RocketOutlined,
  DiffOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { motion } from 'framer-motion';
import { animations } from '../styles/animations';
import { theme } from '../styles/theme';
import axios from 'axios';
import API_URL from '../config/api';

const { Title, Paragraph, Text } = Typography;

const HomePage = ({ onNavigate }) => {
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState({ total: 0, analyzed: 0, compared: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/documents`);
      const docs = response.data.documents || [];
      setDocuments(docs.slice(0, 5)); // Show 5 most recent

      setStats({
        total: docs.length,
        analyzed: docs.length,
        compared: docs.filter(d => d.is_sample).length
      });
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: <FileTextOutlined style={{ fontSize: '32px', color: theme.colors.primary }} />,
      title: 'AI Extraction',
      description: 'Automatically extract parties, amounts, dates, and covenants from loan agreements'
    },
    {
      icon: <RocketOutlined style={{ fontSize: '32px', color: theme.colors.primary }} />,
      title: 'Plain-English Summaries',
      description: 'Convert complex legal documents into clear, readable summaries for stakeholders'
    },
    {
      icon: <DiffOutlined style={{ fontSize: '32px', color: theme.colors.primary }} />,
      title: 'Document Comparison',
      description: 'Compare two versions and identify key changes, additions, and deletions instantly'
    }
  ];

  return (
    <motion.div {...animations.fadeIn}>
      <div style={{ marginBottom: '32px' }}>
        <Title level={2}>
          Welcome to LoanLens
        </Title>
        <Paragraph type="secondary" style={{ fontSize: '16px' }}>
          AI-powered document intelligence for loan professionals. Analyze agreements, extract key terms, and compare versions in seconds.
        </Paragraph>
      </div>

      {/* Stats Cards */}
      <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
        <Col xs={24}>
          <motion.div {...animations.slideUp}>
            <Card hoverable className="hover-lift">
              <Statistic
                title="Total Documents"
                value={stats.total}
                prefix={<FileTextOutlined />}
                valueStyle={{ color: theme.colors.primary }}
              />
            </Card>
          </motion.div>
        </Col>
      </Row>

      {/* Features */}
      <Title level={4} style={{ marginBottom: '16px' }}>
        Key Features
      </Title>
      <Row gutter={[24, 24]} style={{ marginBottom: '32px' }}>
        {features.map((feature, index) => (
          <Col xs={24} md={8} key={index}>
            <motion.div
              {...animations.slideUp}
              transition={{ delay: index * 0.1 }}
            >
              <Card hoverable className="hover-lift" style={{ height: '100%' }}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  {feature.icon}
                  <Title level={5} style={{ margin: 0 }}>
                    {feature.title}
                  </Title>
                  <Text type="secondary">{feature.description}</Text>
                </Space>
              </Card>
            </motion.div>
          </Col>
        ))}
      </Row>

      {/* Quick Actions */}
      <Title level={4} style={{ marginBottom: '16px' }}>
        Quick Actions
      </Title>
      <Row gutter={[16, 16]} style={{ marginBottom: '32px' }}>
        <Col>
          <Button
            type="primary"
            size="large"
            icon={<FileTextOutlined />}
            onClick={() => onNavigate('analyze')}
          >
            Analyze New Document
          </Button>
        </Col>
        <Col>
          <Button
            size="large"
            icon={<DiffOutlined />}
            onClick={() => onNavigate('compare')}
          >
            Compare Documents
          </Button>
        </Col>
      </Row>

      {/* Recent Documents */}
      <Title level={4} style={{ marginBottom: '16px' }}>
        Recent Documents
      </Title>
      <Card loading={loading}>
        <List
          dataSource={documents}
          locale={{ emptyText: 'No documents yet. Upload one to get started!' }}
          renderItem={(doc) => (
            <List.Item
              actions={[
                <Button
                  type="link"
                  onClick={() => onNavigate('analyze')}
                >
                  View
                </Button>
              ]}
            >
              <List.Item.Meta
                avatar={<FileTextOutlined style={{ fontSize: '24px', color: theme.colors.primary }} />}
                title={doc.filename}
                description={
                  <Space>
                    <Text type="secondary">
                      {new Date(doc.upload_date).toLocaleDateString()}
                    </Text>
                    {doc.is_sample && <Tag color="blue">Sample</Tag>}
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      </Card>
    </motion.div>
  );
};

export default HomePage;
